import React, { useMemo, useState } from 'react';
import { getImageURL } from '../utils/api';

export const HOD_STATUS_META = {
  pending: {
    label: 'Pending',
    badge: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
    dot: 'bg-amber-400',
    accent: 'border-l-amber-400',
  },
  'in process': {
    label: 'In process',
    badge: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200',
    dot: 'bg-blue-500',
    accent: 'border-l-blue-500',
  },
  solved: {
    label: 'Solved',
    badge: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
    dot: 'bg-emerald-500',
    accent: 'border-l-emerald-500',
  },
};

export function HodStatusBadge({ status, className = '' }) {
  const meta = HOD_STATUS_META[status] || HOD_STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.badge} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

export function hodFullDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

export function hodTimeAgo(value) {
  if (!value) return '';
  const diffSec = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (diffSec < 45) return 'just now';
  if (diffSec < 3600) return `${Math.max(1, Math.round(diffSec / 60))}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.round(diffSec / 86400)}d ago`;
  return hodFullDate(value);
}

const AVATAR_COLORS = ['bg-indigo-500', 'bg-emerald-500', 'bg-rose-500', 'bg-amber-500', 'bg-cyan-600', 'bg-violet-500'];

export function HodAvatar({ person, size = 'h-9 w-9', textSize = 'text-sm' }) {
  const [broken, setBroken] = useState(false);
  const name = person?.name || '?';
  if (person?.imagePath && !broken) {
    return (
      <img
        src={getImageURL(person.imagePath)}
        alt={name}
        onError={() => setBroken(true)}
        className={`${size} shrink-0 rounded-full object-cover`}
      />
    );
  }
  const color = AVATAR_COLORS[(person?.staffId || name.length) % AVATAR_COLORS.length];
  return (
    <div className={`${size} ${textSize} ${color} flex shrink-0 items-center justify-center rounded-full font-bold text-white`}>
      {name.trim().slice(0, 1).toUpperCase()}
    </div>
  );
}

function RoleChip({ role }) {
  return role === 'hod' ? (
    <span className="rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">HOD</span>
  ) : (
    <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-600">Lecturer</span>
  );
}

// Facebook-style conversation for one message: the original post, then a
// single time-ordered feed of replies (bubbles) and status changes (small
// centered notes), then a composer. Used by both the lecturer page and the
// HOD inbox so the thread reads the same on both sides.
export default function HodThread({
  notification,
  currentStaffId,
  isSuperAdmin = false,
  viewerIsHod = false,
  onComment,
  onDeleteComment,
  composerPlaceholder,
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const feed = useMemo(() => {
    const items = [];
    notification.comments.forEach((c) => items.push({ kind: 'comment', at: new Date(c.createdAt).getTime(), key: `c${c.commentId}`, data: c }));
    // The first log row is just "message created" -- the post itself already
    // says that, so only later status changes appear inline.
    notification.statusLog.slice(1).forEach((l) => items.push({ kind: 'status', at: new Date(l.changedAt).getTime(), key: `s${l.logId}`, data: l }));
    return items.sort((a, b) => a.at - b.at);
  }, [notification.comments, notification.statusLog]);

  const submit = async () => {
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    try {
      const ok = await onComment(notification.notificationId, value);
      if (ok !== false) setText('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {feed.length === 0 ? (
        <p className="py-3 text-center text-xs text-gray-400">
          {viewerIsHod ? 'No replies yet — post an update so the lecturer knows it is being looked at.' : 'No replies yet. The HOD will respond here.'}
        </p>
      ) : (
        <div className="space-y-3 py-1">
          {feed.map((item) => {
            if (item.kind === 'status') {
              const l = item.data;
              return (
                <div key={item.key} className="flex items-center justify-center gap-2 text-[11px] text-gray-400">
                  <span className="h-px w-8 bg-gray-200" />
                  <span title={hodFullDate(l.changedAt)}>
                    {l.changer?.name || 'HOD'} marked this <HodStatusBadge status={l.status} className="mx-0.5 align-middle" /> · {hodTimeAgo(l.changedAt)}
                  </span>
                  <span className="h-px w-8 bg-gray-200" />
                </div>
              );
            }
            const c = item.data;
            const mine = c.author?.staffId && c.author.staffId === currentStaffId;
            const canDelete = isSuperAdmin || mine;
            const isHodBubble = c.authorRole === 'hod';
            return (
              <div key={item.key} className="flex items-start gap-2.5">
                <HodAvatar person={c.author} size="h-8 w-8" textSize="text-xs" />
                <div className="min-w-0 max-w-[85%]">
                  <div className={`rounded-2xl px-3.5 py-2 ${isHodBubble ? 'bg-indigo-50 ring-1 ring-indigo-100' : 'bg-gray-100'}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-semibold text-gray-900">{c.author?.name || 'Unknown'}</span>
                      <RoleChip role={c.authorRole} />
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-800">{c.comment}</p>
                  </div>
                  <div className="mt-1 flex items-center gap-3 pl-3 text-[11px] text-gray-400">
                    <span title={hodFullDate(c.createdAt)}>{hodTimeAgo(c.createdAt)}</span>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => onDeleteComment(notification.notificationId, c.commentId)}
                        className="font-semibold hover:text-red-600"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex items-end gap-2.5 border-t border-gray-100 pt-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          maxLength={2000}
          placeholder={composerPlaceholder || 'Write a comment…  (Enter to post, Shift+Enter for a new line)'}
          className="min-h-[44px] flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
        />
        <button
          type="button"
          onClick={submit}
          disabled={sending || !text.trim()}
          className="shrink-0 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {sending ? 'Posting…' : 'Post'}
        </button>
      </div>
    </div>
  );
}
